import { useSEO } from "@/hooks/useSEO";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import LoyaltyLookup from "@/components/LoyaltyLookup";
import { ArrowRight, Star, CreditCard } from "lucide-react";

const TIERS = [
  { tier: "Island Guest", pct: "−2%", need: "3–6 completed rentals", highlight: false, perks: ["2% off every rental — applied automatically at checkout"] },
  { tier: "Silver Explorer", pct: "−5%", need: "7+ completed rentals", highlight: false, perks: ["5% off every rental — applied automatically at checkout", "Early pickup reservations (subject to availability)"] },
  { tier: "Gold Voyager", pct: "−10%", need: "20+ completed rentals", highlight: true, perks: ["10% off every rental — applied automatically at checkout", "GPS navigation unit half price (subject to availability)", "Baby car seat half price (subject to availability)", "1 hour late return free", "Early pickup reservations", "Free vehicle upgrades (subject to availability)", "Additional driver half price"] },
  { tier: "Platinum Islander", pct: "−15%", need: "33+ completed rentals", highlight: false, perks: ["15% off every rental — applied automatically at checkout", "No security deposit — deposit-free rentals", "Free GPS navigation unit (subject to availability)", "Free baby car seat (subject to availability)", "2 hours late return free", "Early pickup at no extra charge", "Free vehicle upgrades", "Free additional driver"] },
];

export default function Loyalty() {
  useSEO({ title: "Car Rental Loyalty Program Nassau — Up To 15% Off | Easy Drive", description: "Join Easy Drive Loyalty free: rentals count toward tiers from Island Guest (2% off) to Platinum Islander (15% off + free GPS & baby seat). Discounts apply automatically.", path: "/loyalty" });
  const [loyalName, setLoyalName] = useState("");
  const [loyalEmail, setLoyalEmail] = useState("");
  const [loyalJoined, setLoyalJoined] = useState(null);
  const [loyalSaving, setLoyalSaving] = useState(false);

  const joinLoyalty = async () => {
    if (!loyalName.trim() || !/\S+@\S+\.\S+/.test(loyalEmail)) return;
    setLoyalSaving(true);
    try {
      const { data } = await api.post("/loyalty/signup", { name: loyalName.trim(), email: loyalEmail.trim() });
      setLoyalJoined(data);
    } catch (e) {
      setLoyalJoined({ error: true });
    } finally {
      setLoyalSaving(false);
    }
  };

  return (
    <div data-testid="loyalty-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Easy Drive Loyalty</p>
        <h1 className="mt-3 font-display text-5xl sm:text-6xl tracking-tight">
          The more you roam, <span className="italic gold-text">the more you save</span>
        </h1>
        <p className="mt-5 text-muted-foreground max-w-xl leading-relaxed">
          Every completed rental moves you up a tier. Your discount applies automatically at
          checkout — no codes, no cards, just come back.
        </p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Book & drive", d: "Every confirmed rental counts — guests included, no signup needed." },
            { n: "02", t: "Level up automatically", d: "Hit 7, 20 or 33 rentals and your discount unlocks itself at checkout." },
            { n: "03", t: "Enjoy the perks", d: "Free upgrades, late-return grace, cheaper drivers — plus a loyalty card for your phone." },
          ].map((s, i) => (
            <div key={i} data-testid={`loyalty-step-${i}`} className="rounded-2xl border border-border bg-white p-6">
              <p className="font-display text-2xl gold-text">{s.n}</p>
              <p className="mt-2 font-display text-lg tracking-tight">{s.t}</p>
              <p className="mt-1.5 text-xs text-muted-foreground font-ui leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map((t, i) => (
            <div key={i} data-testid={`loyalty-tier-${i}`} className={`rounded-2xl border p-7 transition-transform duration-300 hover:-translate-y-1 ${t.highlight ? "border-primary bg-primary/5 shadow-[0_10px_30px_rgba(199,30,46,0.12)]" : "border-border bg-white"}`}>
              <p className={`font-display text-3xl tracking-tight ${t.highlight ? "gold-text" : ""}`}>{t.pct}</p>
              <p className="mt-2 font-display text-xl tracking-tight">{t.tier}</p>
              <p className="mt-1.5 text-xs text-muted-foreground font-ui">{t.need}</p>
              {t.perks.length > 0 && (
                <ul data-testid={`loyalty-tier-perks-${i}`} className="mt-3 space-y-1.5">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-1.5 text-xs font-ui text-foreground/80"><Star size={11} className="gold-text mt-0.5 shrink-0" /> {p}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div data-testid="loyalty-signup" className="mt-10 bg-white border border-border rounded-2xl p-6 flex flex-wrap items-center gap-4">
          <p className="font-display text-xl tracking-tight">Join free for cheaper rates on every rental</p>
          <div className="flex flex-wrap items-center gap-3 lg:ml-auto">
            {loyalJoined && !loyalJoined.error ? (
              <p data-testid="loyalty-signup-success" className="text-sm font-ui text-emerald-600">
                {loyalJoined.needs_verification
                  ? "Almost there — we emailed you a verification link. Tap it to activate your membership."
                  : `Welcome aboard${loyalJoined.tier ? ` — you're ${loyalJoined.tier}${loyalJoined.percent ? ` (−${loyalJoined.percent}%)` : ""}` : ""}! Check your inbox.`}
              </p>
            ) : (
              <>
                <input data-testid="loyalty-signup-name" value={loyalName} onChange={(e) => setLoyalName(e.target.value)} placeholder="Your name" className="h-10 rounded-lg border border-input bg-white px-3 text-sm font-ui w-44 hover:border-primary/50 transition-colors duration-200" />
                <input data-testid="loyalty-signup-email" type="email" value={loyalEmail} onChange={(e) => setLoyalEmail(e.target.value)} placeholder="you@email.com" className="h-10 rounded-lg border border-input bg-white px-3 text-sm font-ui w-56 hover:border-primary/50 transition-colors duration-200" />
                <Button data-testid="loyalty-signup-button" onClick={joinLoyalty} disabled={loyalSaving} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-10 px-5 text-sm">
                  {loyalSaving ? "Joining…" : "Join Loyalty"}
                </Button>
              </>
            )}
          </div>
        </div>

        <LoyaltyLookup />

        <p className="mt-4 text-xs text-muted-foreground font-ui">
          Tiers are based on completed rentals per email address — members and guests both qualify automatically.
        </p>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Link to="/loyalty-card">
            <Button data-testid="loyalty-page-card-button" variant="outline" className="rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui">
              <CreditCard size={14} className="mr-2" /> Print My Loyalty Card
            </Button>
          </Link>
          <Link data-testid="loyalty-page-book-link" to="/fleet" className="text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2">
            Book & start earning <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}
